import { useEffect, useRef } from 'react';
import { AppWindow, ArrowLeft, Home, Loader2, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { sendDeviceKey } from '@/services/vmos-api';
import { LiveVMOSScreenshot } from './LiveVMOSScreenshot';
import { getStatusBadge, getMessageIcon, getMessageStyle } from './utils';
import type { DeviceStreamState } from './shared-types';

export const DeviceDetailSheet = ({
  padId,
  deviceName,
  state,
  open,
  onOpenChange,
}: {
  padId: string;
  deviceName: string;
  state: DeviceStreamState;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [state.messages.length]);

  const isRunning = state.status === 'running';

  const sendNav = async (keycode: number) => {
    try {
      const res = await sendDeviceKey(padId, keycode);
      if (!res?.results?.[padId]) {
        toast.error('Key event failed');
      }
    } catch (e: any) {
      toast.error(e?.message || 'Key event failed');
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[90vw] sm:w-[600px] sm:max-w-[600px] flex flex-col p-0">
        <SheetHeader className="px-4 py-3 border-b shrink-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Smartphone className="h-4 w-4 text-muted-foreground shrink-0" />
              <SheetTitle className="text-sm font-medium truncate">{deviceName}</SheetTitle>
            </div>
            <div className="flex items-center gap-2.5 shrink-0">
              {state.step > 0 && isRunning && (
                <span className="text-[11px] text-muted-foreground font-mono tabular-nums">
                  {state.step}/{state.maxSteps}
                </span>
              )}
              {getStatusBadge(state.status)}
            </div>
          </div>
          <SheetDescription className="sr-only">
            Live view for {deviceName}
          </SheetDescription>
        </SheetHeader>

        <div className="px-4 pt-3 shrink-0">
          <LiveVMOSScreenshot
            padId={padId}
            disabled={!isRunning}
            className="h-[300px]"
          />
        </div>

        <div className="px-4 py-2 shrink-0 flex items-center justify-center gap-2">
          <Button size="sm" variant="outline" onClick={() => sendNav(4)} title="Back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <Button size="sm" variant="outline" onClick={() => sendNav(3)} title="Home">
            <Home className="h-4 w-4 mr-1" /> Home
          </Button>
          <Button size="sm" variant="outline" onClick={() => sendNav(187)} title="Recents">
            <AppWindow className="h-4 w-4 mr-1" /> Recents
          </Button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-1.5">
          {state.messages.length === 0 && isRunning && (
            <div className="flex items-center gap-2 text-muted-foreground text-xs p-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              Starting agent...
            </div>
          )}
          {state.messages.map((msg) => (
            <div
              key={msg.id}
              className={cn(
                'px-2.5 py-1.5 rounded-md border text-xs',
                getMessageStyle(msg.type),
              )}
            >
              <div className="flex items-center gap-1.5 mb-0.5 text-muted-foreground">
                {getMessageIcon(msg.type)}
                <span className="capitalize text-[10px] font-medium">{msg.type}</span>
                <span className="ml-auto text-[10px]">
                  {msg.timestamp.toLocaleTimeString()}
                </span>
              </div>
              <p className="whitespace-pre-wrap break-words leading-relaxed">{msg.content}</p>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};
